/**
 * The access context and its hooks, apart from the provider component.
 *
 * Same Fast Refresh reason as the theme package: a module exporting both a
 * component and a hook loses component state on every edit in development.
 */

import { createContext, useContext } from "react";

import { AccessSnapshot } from "./snapshot";

export interface AccessContextValue {
  snapshot: AccessSnapshot;
  /** False while permissions are being fetched. */
  ready: boolean;
}

export const AccessContext = createContext<AccessContextValue>({
  // Fail-closed default: a component rendered outside a provider — or before
  // permissions arrive — shows nothing rather than everything. The opposite
  // default flashes an admin menu at every user for one frame.
  snapshot: AccessSnapshot.empty(),
  ready: false,
});

export function useAccess(): AccessContextValue {
  return useContext(AccessContext);
}

/**
 * Whether the current principal holds a permission.
 *
 * Returns false while permissions are loading. A component that must
 * distinguish "not allowed" from "not known yet" — to show a skeleton rather
 * than an empty state — should read `ready` from {@link useAccess} instead.
 */
export function useCan(...permissions: string[]): boolean {
  const { snapshot, ready } = useAccess();
  if (!ready) return false;
  return snapshot.canAllOf(permissions);
}

/** Whether the principal holds at least one of the permissions. */
export function useCanAny(...permissions: string[]): boolean {
  const { snapshot, ready } = useAccess();
  if (!ready) return false;
  return snapshot.canAnyOf(permissions);
}
